/**
 * Location utility functions for OpenStreetMap integration
 */

/**
 * Reverse geocode coordinates to get formatted address using Nominatim (OpenStreetMap)
 * @param {Object} position - { lat: number, lng: number }
 * @returns {Promise<string>} Formatted address
 */
export const reverseGeocode = async (position) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?` +
      `format=json&lat=${position.lat}&lon=${position.lng}&` +
      `addressdetails=1&accept-language=en`,
      {
        headers: {
          'User-Agent': 'Taealam Education Platform'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Geocoding failed: ${response.status}`);
    }

    const data = await response.json();

    if (data.display_name) {
      return data.display_name;
    } else {
      throw new Error('No address found for these coordinates');
    }
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    throw error;
  }
};

/**
 * Calculate distance between two points using Haversine formula
 * @param {number} lat1 - First point latitude
 * @param {number} lon1 - First point longitude
 * @param {number} lat2 - Second point latitude
 * @param {number} lon2 - Second point longitude
 * @returns {number} Distance in kilometers
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Convert degrees to radians
 * @param {number} value - Degrees
 * @returns {number} Radians
 */
const toRad = (value) => {
  return value * Math.PI / 180;
};

/**
 * Format distance for display
 * @param {number} distanceKm - Distance in kilometers
 * @param {boolean} isArabic - Use Arabic formatting
 * @returns {string} Formatted distance string
 */
export const formatDistance = (distanceKm, isArabic = false) => {
  if (distanceKm < 1) {
    const meters = Math.round(distanceKm * 1000);
    return isArabic ? `${meters} متر` : `${meters}m`;
  }
  return isArabic ? `${distanceKm.toFixed(1)} كم` : `${distanceKm.toFixed(1)}km`;
};

/**
 * Validate if coordinates are valid
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {boolean} True if valid coordinates
 */
export const isValidCoordinate = (lat, lng) => {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    lat >= -90 && lat <= 90 &&
    lng >= -180 && lng <= 180 &&
    lat !== 0 && lng !== 0
  );
};

/**
 * Get Bahrain center coordinates
 * @returns {Object} { lat, lng }
 */
export const getBahrainCenter = () => ({
  lat: 26.0667,
  lng: 50.5577
});

/**
 * Create Google Maps URL from coordinates
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {string} Google Maps URL
 */
export const createMapsUrl = (lat, lng) => {
  return `https://www.google.com/maps?q=${lat},${lng}`;
};

/**
 * Create Google Maps search URL from address text
 * Use this when you only have an address string (no coordinates)
 * @param {string} address - Address text to search
 * @returns {string} Google Maps search URL
 */
export const createMapsSearchUrl = (address) => {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
};
